import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useProjectsViews } from '../hooks/useProjectsViews';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../hooks/usePlatform';
import { PLANBASE_CONFIG } from '../config';
import { BannerCarousel } from '../components/BannerCarousel';
import { resolveUserTier } from '../lib/plan';
import { ProjectCard } from '../components/ProjectCard';
import { Button } from '../components/ui/button';
import { SkeletonCard as UISkeletonCard, SkeletonImage } from '../components/ui/skeleton';

import { LockClosedIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';

// ─── Drawing type image card ──────────────────────────────────────────────────
function DrawingTypeCard({ image, onClick, isLocked = false }: { image: any; onClick: () => void; isLocked?: boolean }) {
  return (
    <div
      className={`relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group ${isLocked ? 'opacity-60' : ''}`}
      onClick={isLocked ? undefined : onClick}
    >
      <img
        src={image.url}
        alt={image.title}
        loading="lazy"
        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${isLocked ? 'blur-sm' : ''}`}
      />
      {isLocked ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <LockClosedIcon className="h-6 w-6 text-white" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-white text-sm font-medium line-clamp-1">{image.title}</p>
            {image.projectTitle && <p className="text-white/80 text-xs">{image.projectTitle}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export the shared UI primitive under the local name so downstream
// call sites don't need to change — SkeletonCard is now the tokenised
// version from components/ui/skeleton.
const SkeletonCard = UISkeletonCard;

// ─── Filter pills ─────────────────────────────────────────────────────────────
function FilterPills({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={scrollRef} className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      <button
        onClick={() => onChange('all')}
        className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
          value === 'all' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
        }`}
      >
        All
      </button>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors capitalize flex-shrink-0 ${
            value === opt.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = usePlatform();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDrawingType, setSelectedDrawingType] = useState('all');
  const [activeTab, setActiveTab] = useState<'projects' | 'drawings'>('projects');

  const userPlan = resolveUserTier(user);
  const freeUserLimit = PLANBASE_CONFIG.planLimits.free.canViewDiscovery;

  const { projects, loading, error } = useProjects({
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    status: 'published',
    limit: 100,
  });

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { viewsMap } = useProjectsViews(projectIds);

  const filteredProjects = projects.filter((project) => {
    if (project.data.isLocked) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      project.data.title?.toLowerCase().includes(q) ||
      project.data.architect?.toLowerCase().includes(q) ||
      project.data.location?.toLowerCase().includes(q) ||
      project.data.description?.toLowerCase().includes(q)
    );
  });

  const drawingTypeImages = projects.flatMap((project) => {
    if (project.data.isLocked || !project.data.images) return [];
    return project.data.images
      .filter((img: any) => typeof img === 'object' && img.title)
      .map((img: any) => ({
        ...img,
        projectId: project.id,
        projectSlug: project.slug,
        projectTitle: project.data.title,
        drawingType: detectDrawingType(img.title),
      }));
  });

  const filteredDrawingImages = selectedDrawingType === 'all'
    ? drawingTypeImages
    : drawingTypeImages.filter((img) => img.drawingType === selectedDrawingType);

  const categoryOptions = PLANBASE_CONFIG.categories.map((c) => ({ value: c.value, label: c.label }));
  const drawingTypeOptions = PLANBASE_CONFIG.drawingTypes;

  const showUpgradePrompt = userPlan === 'free' && filteredProjects.length > freeUserLimit;

  return (
    <div className="space-y-3 md:space-y-4">

      {/*
        ── Header — single markup, responsive breakpoints ──────────────
        Previously two full JSX branches (mobile / desktop) with ~90 lines
        of duplicated markup, different search styling, and a Projects /
        Drawings tab switcher that only rendered on mobile. Collapsed to
        one header that adapts by breakpoint; the tab switcher now works
        on desktop too so users on wide screens can jump to Drawings.
      */}
      <div className="sticky top-0 z-20 -mx-3 border-b border-border bg-background/95 px-3 pb-2 pt-3 backdrop-blur-sm md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:pb-0 md:pt-0 md:backdrop-blur-none">
        <div className="mb-2 flex items-center justify-between gap-3 md:mb-0">
          <h1 className="text-sm font-semibold uppercase tracking-wide text-foreground">Discover</h1>
          {/* Small upload FAB on mobile → real button on desktop. */}
          <button
            type="button"
            onClick={() => navigate('/portfolio/new')}
            aria-label="Upload project"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-95 md:hidden"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
          <Button
            onClick={() => navigate('/portfolio/new')}
            className="hidden bg-primary text-primary-foreground hover:bg-primary/90 md:inline-flex"
          >
            <PlusIcon className="mr-1.5 h-4 w-4" />
            Upload Project
          </Button>
        </div>

        {/* Search — visible on mobile, hidden on desktop (desktop has one inside the panel). */}
        <div className="relative mb-2 md:hidden">
          <input
            type="search"
            inputMode="search"
            placeholder="Search projects..."
            aria-label="Search projects"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full rounded-full border border-input bg-muted pl-8 pr-3 text-sm text-foreground focus:border-primary focus:outline-none"
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>

        {/* Filter pills — hidden on desktop; desktop shows them inside the panel header. */}
        <div className="md:hidden">
          <FilterPills options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} />
        </div>

        {/*
          Projects / Drawings tab switcher — now shown on both breakpoints.
          Underlined-tab pattern; keyboard-accessible; aria-selected + role.
        */}
        <div role="tablist" aria-label="Discover mode" className="-mx-3 mt-2 flex gap-0 border-b border-border px-3 md:mx-0 md:mt-3 md:gap-4 md:px-0">
          {(['projects', 'drawings'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 pb-2 text-xs font-medium capitalize transition-colors md:flex-none md:px-1 md:text-sm ${
                  isActive
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            );
          })}

          {/*
            Preview chip — names the free-user state ("Preview only"),
            with a text link to upgrade. Only shows when the free-plan
            gate actually clips content on this page.
          */}
        </div>
      </div>
      <BannerCarousel />

      {/* ── Projects panel ──────────────────────────────────────────────── */}
      {(!isMobile || activeTab === 'projects') && (
      <div className="md:bg-card md:rounded-2xl md:border md:border-border overflow-hidden">

        {/* Desktop sticky panel header */}
        {!isMobile && (
          <div className="sticky top-0 z-10 bg-[#f0f0f0] dark:bg-muted/40 border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <FilterPills options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} />
              <div className="relative w-56 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Search projects..."
                  aria-label="Search projects"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 h-8 border border-input rounded-full bg-muted text-foreground text-xs focus:outline-none focus:border-primary"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="md:p-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-sm">{error}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <MagnifyingGlassIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No projects found. Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
              {filteredProjects.map((project, index) => {
                const isLocked = userPlan === 'free' && index >= freeUserLimit;
                return (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onClick={() => navigate(`/project/${project.slug || project.id}`)}
                    isLocked={isLocked}
                    viewCount={viewsMap[project.id] || 0}
                  />
                );
              })}
            </div>
          )}

          {showUpgradePrompt && (
            <div className="text-center py-8 mt-4 bg-orange-50 dark:bg-orange-950/30 rounded-xl">
              <LockClosedIcon className="h-7 w-7 text-primary mx-auto mb-2" />
              <h3 className="text-sm font-medium text-foreground mb-1">Unlock More Projects</h3>
              <p className="text-xs text-muted-foreground">Upgrade to Pro to view unlimited projects</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Drawing types panel ─────────────────────────────────────────── */}
      {(!isMobile || activeTab === 'drawings') && (
      <div className="md:bg-card md:rounded-2xl md:border md:border-border overflow-hidden">

        <div className="md:sticky md:top-0 z-10 md:bg-[#f0f0f0] md:dark:bg-muted/40 md:border-b md:border-border px-0 md:px-4 py-2 md:py-3">
          <div className="flex items-center gap-3">
            {!isMobile && <span className="text-xs font-medium text-foreground flex-shrink-0">Drawing Type</span>}
            <FilterPills options={drawingTypeOptions} value={selectedDrawingType} onChange={setSelectedDrawingType} />
          </div>
        </div>

        <div className="md:p-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonImage key={i} aspect="4/3" radius="xl" />
              ))}
            </div>
          ) : filteredDrawingImages.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {filteredDrawingImages.map((image, index) => {
                const isLocked = userPlan === 'free' && index >= freeUserLimit;
                return (
                  <DrawingTypeCard
                    key={`${image.projectId}-${index}`}
                    image={image}
                    onClick={() => navigate(`/project/${image.projectSlug || image.projectId}`)}
                    isLocked={isLocked}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-sm">No drawings found.</p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function detectDrawingType(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('floor plan') || t.includes('floorplan')) return 'floor-plan';
  if (t.includes('roof plan') || t.includes('roofplan')) return 'roof-plan';
  if (t.includes('site plan') || t.includes('siteplan')) return 'site-plan';
  if (t.includes('elevation')) return 'elevation';
  if (t.includes('section')) return 'section';
  if (t.includes('perspective') || t.includes('3d') || t.includes('render')) return 'perspective';
  if (t.includes('mechanical') || t.includes('hvac') || t.includes('plumbing') || t.includes('electrical')) return 'mechanical-drawing';
  return 'others';
}
